import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, Observable, map, switchMap, catchError, tap } from 'rxjs';
import { VehicleService } from '../_services/vehicle.service';
import { Injectable } from '@angular/core';
import * as ActionConfigurationManager from '../_actions/config.actions';
import { Vehicle, VinDecodeResponse } from '../_models/vehicle';
import { decodeVinGetCarInfo, decodeVinGetCarInfoFail, decodeVinGetCarInfoSuccess, vinDecodedSuccess, vinDecodeStateTrue } from '../_actions/vinDecode.actions';
import { Action, Store } from '@ngrx/store';
import { loadVehicle, loadVehicleImage, updateOdometer, updateVIN } from '../_actions/vehicle.actions';
import { invalidState, productExist, ratesError, success, hasExistingClaims, mileageOrAgeExceeded, ncesiMileageOrAgeExceeded } from '../_actions/modal-component.actions';
import { hideInlineLoader } from '../_actions/inline-loader.actions';
import { ratesLoaded, ratesLoadedFailed } from '../_actions/rates.actions';
import { VINDecodeCarInfoRequest, VINDecodeCarInfoResponse, VINDecodeRequest } from '../_models/vin-decode';
import brandInfo from '../../environments/brand.json'
import { clearCart } from '../_actions/cart.actions';
import {
  isHppGenesisServiceDriveMultiProduct,
  shouldDeferHppGenesisServiceDriveProductOutcome,
  resolveHyundaiServiceDriveAfterLoop,
  applyHyundaiServiceDriveOutcome,
} from './hpp-genesis-rates.helpers';


@Injectable()

export class VinDecodeEffects {

    public verifyVin: Observable<Action> = createEffect(() =>
        this.actions$
            .pipe(
                ofType(ActionConfigurationManager.DECODE_VIN_VERIFY_VIN),
                switchMap((action) => this.vehicleService.vin_decode(action.vin)
                    .pipe(
                        map((vinResponse) => {
                            console.log(vinResponse);
                            let _vehicle: Vehicle = JSON.parse(sessionStorage.getItem('vehicle'))
                            _vehicle.vin = vinResponse.vin
                            this.store.dispatch(updateVIN(_vehicle))
                            this.store.dispatch(vinDecodeStateTrue())
                            return vinDecodedSuccess(vinResponse)
                        }),
                        catchError(() => EMPTY)
                    )
                )
            )
    )

    public getCarInfo: Observable<Action> = createEffect(() =>
        this.actions$
            .pipe(
                ofType(ActionConfigurationManager.DECODE_VIN_GET_CAR_INFO),
                switchMap((action) => this.vehicleService.getVinCarInfo(action.vin)
                    .pipe(
                        map((vinResponse: any) => {
                            console.log(vinResponse.data)
                            const vehicle: VINDecodeCarInfoResponse = {
                                vin: action.vin,
                                make: vinResponse.data.finalVinData.make,
                                model: vinResponse.data.finalVinData.model,
                                year: vinResponse.data.finalVinData.modelYear,
                                inserviceDate: vinResponse.data.inserviceDate ? vinResponse.data.inserviceDate : null
                            }
                            const obj: Vehicle = {
                                vin: action.vin,
                                make: vinResponse.data.finalVinData.make,
                                model: vinResponse.data.finalVinData.model,
                                year: vinResponse.data.finalVinData.modelYear,
                                registeredState: JSON.parse(sessionStorage.getItem('dealer')).data.address.state,
                                isElectric: (vinResponse.data.finalVinData.fuelType && // Put here to try and figure out fuel type
                                    vinResponse.data.finalVinData.fuelType.toLowerCase().includes('g') ||
                                    vinResponse.data.finalVinData.fuelType.toLowerCase().includes('diesel')) ? false : true
                            }
                            console.log("obj ", obj)
                            this.store.dispatch(loadVehicle(obj))
                            return decodeVinGetCarInfoSuccess(vehicle)
                        }),
                        catchError(() => {
                            this.store.dispatch(decodeVinGetCarInfoFail());
                            return EMPTY;
                        })
                    )
                )
            ))

    private checkForExistingVehicle(vehicleData) {
        const exisitingVehicle = JSON.parse(sessionStorage.getItem('oldVehicle'));
        if(exisitingVehicle && exisitingVehicle.vin !== vehicleData.vin) {
            this.store.dispatch(clearCart());
            sessionStorage.removeItem('oldVehicle');
        }
    };

    public decodeVINGetSubscriptionRates: Observable<Action> = createEffect(() =>
        this.actions$
            .pipe(
                ofType(ActionConfigurationManager.DECODE_VIN_GET_SUBSCRIPTION_RATES),
                tap(action => this.checkForExistingVehicle(action)),
                switchMap((action) => this.vehicleService.lookupSubscriptionRates(action)
                    .pipe(
                        map((rates) => {
                            const product = rates?.data?.productDetailsList?.[0];
                            if (product?.statusCode === 100) {
                                console.log('[subscription-flow] rates accepted', {
                                    statusCode: product.statusCode,
                                    planCodes: product.productPlanList?.map((plan) => plan.planCode) ?? [],
                                    getRatesPage: action.getRatesPage,
                                    appFlowState: sessionStorage.getItem('appFlowState'),
                                });
                                this.storeSubscriptionVehicle(action, rates);
                                this.store.dispatch(ratesLoaded(rates));
                                sessionStorage.setItem('page', 'ratesPage');
                                return success();
                            }

                            if (product?.statusCode === 105) {
                                console.log('[subscription-flow] vehicle ineligible', {
                                    statusCode: product.statusCode,
                                    message: product.message,
                                    vehicleYear: rates?.data?.vehicleYear ?? null,
                                    vehicleAge: rates?.data?.vehicleAge ?? null,
                                    odometer: rates?.data?.odometer ?? null,
                                });
                                this.store.dispatch(ratesLoadedFailed(rates));
                                this.store.dispatch(hideInlineLoader());
                                return mileageOrAgeExceeded();
                            }

                            console.log('[subscription-flow] rates not loaded', {
                                statusCode: product?.statusCode ?? null,
                                message: product?.message ?? null,
                                appFlowState: sessionStorage.getItem('appFlowState'),
                            });
                            this.store.dispatch(ratesLoadedFailed(rates));
                            this.store.dispatch(hideInlineLoader());
                            return ratesError();
                        }),
                        catchError((error) => {
                            console.error('[subscription-flow] rates effect failed', {
                                message: error?.message ?? null,
                            });
                            this.store.dispatch(hideInlineLoader());
                            this.store.dispatch(ratesError());
                            return EMPTY;
                        })
                    )
                )
            )
    )

    private storeSubscriptionVehicle(action, rates): void {
        const existing: Vehicle = JSON.parse(sessionStorage.getItem('vehicle'));
        const data = rates?.data || {};
        const vehicle: Vehicle = {
            ...(existing || {
                makeExternalId: '',
                makeSfId: '',
                modelExternalId: '',
                modelSfId: '',
                modelNumber: '',
                purchaseDate: '',
                referenceNumber1: '',
                referenceNumber2: '',
                referenceNumber3: '',
                referenceNumber4: '',
                trimExternalId: '',
                trimSfId: '',
                vehicleSfId: '',
                vehicleCondition: '',
                vehicleExternalId: '',
                vehicleType: '',
                deliveryDate: '',
                exteriorColor: '',
                mfrWarrantyDate: '',
                image: '',
            }),
            registeredState: action.customerState,
            mileage: action.odometer,
            model: data.vehicleModel,
            make: data.vehicleMake,
            year: data.vehicleYear,
            trim: data.vehicleTrim,
            vin: action.vin,
            inserviceDate: data.inServiceDate ? data.inServiceDate : existing?.inserviceDate ?? null,
            isElectric: typeof data.isElectric === 'boolean' ? data.isElectric : existing?.isElectric ?? false,
        };
        this.store.dispatch(updateOdometer(vehicle));
    }

    public decodeVINGetRates: Observable<Action> = createEffect(() => 
        this.actions$
            .pipe(
                ofType(ActionConfigurationManager.DECODE_VIN_GET_RATES),
                tap(action => this.checkForExistingVehicle(action)),
                switchMap((action) => this.vehicleService.decodeVinGetRates(action)
                    .pipe(
                        map((rates) => {
                            console.log("************");
                            console.log("1. loaded rates from api Y:", rates, action);
                            console.log(`2. Found ${rates.data.productDetailsList.length} products`);

                            let _vehicle: Vehicle = JSON.parse(
                                sessionStorage.getItem("vehicle")
                              );
                            // let odometer = action.odometer && typeof action.odometer === 'string' && action.odometer.includes(',') ? action.odometer.replace(',','') : action.odometer
                            for (let i = 0; i < rates.data.productDetailsList.length; i++) {
                                if (rates.data.productDetailsList[i].statusCode === 100) {
                                    // Set the registration State and mileage.
                                    let _vehicle: Vehicle = JSON.parse(sessionStorage.getItem('vehicle'))
                                    if (_vehicle) {
                                        console.log('3. Update state and mileage on vehicle in store:', action)
                                        _vehicle.registeredState = action.customerState
                                        _vehicle.mileage = action.odometer
                                        _vehicle.model = rates.data.vehicleModel
                                        _vehicle.make = rates.data.vehicleMake
                                        _vehicle.year = rates.data.vehicleYear
                                        _vehicle.trim = rates.data.vehicleTrim
                                        _vehicle.vin = action.vin
                                        _vehicle.inserviceDate = rates.data.inServiceDate ? rates.data.inServiceDate : null
                                        _vehicle.isElectric = rates.data.productDetailsList[i].productName.includes('EVEHICLE') || rates.data.productDetailsList[i].productName.includes('EV') ? true : _vehicle.isElectric    
                                        this.store.dispatch(updateOdometer(_vehicle))
                                        console.log('4. Vehicle after action', _vehicle)
                                    } else {
                                        let vehicle: Vehicle = {
                                            registeredState: action.customerState,
                                            mileage: action.odometer,
                                            model: rates.data.vehicleModel,
                                            make: rates.data.vehicleMake,
                                            year: rates.data.vehicleYear,
                                            trim: rates.data.vehicleTrim,
                                            isElectric: rates.data.productDetailsList[i].productName.includes('EVEHICLE') || rates.data.productDetailsList[i].productName.includes('EV') ? true : false,
                                            inserviceDate: rates.data.inServiceDate ? rates.data.inServiceDate : null,
                                            makeExternalId: '',
                                            makeSfId: '',
                                            modelExternalId: '',
                                            modelSfId: '',
                                            modelNumber: '',
                                            purchaseDate: '',
                                            referenceNumber1: '',
                                            referenceNumber2: '',
                                            referenceNumber3: '',
                                            referenceNumber4: '',
                                            trimExternalId: '',
                                            trimSfId: '',
                                            vehicleSfId: '',
                                            vehicleCondition: '',
                                            vehicleExternalId: '',
                                            vehicleType: '',
                                            vin: action.vin,
                                            deliveryDate: '',
                                            exteriorColor: '',
                                            mfrWarrantyDate: '',
                                            image: ''
                                        }
                                        this.store.dispatch(updateOdometer(vehicle))
                                        console.log('4. Vehicle after action', vehicle)
                                    }
                                    this.store.dispatch(ratesLoaded(rates))
                                    // this.store.dispatch(loadVehicleImage(_vehicle));
                                    // this.router.navigate([action.getRatesPage])
                                    sessionStorage.setItem('page', 'ratesPage')

                                    console.log('This is the success action')

                                    if (brandInfo.brand === 'HPP' || brandInfo.brand === 'Genesis' || brandInfo.brand === 'Kia') {
                                        if (rates.product === 'PPM' && rates.data.productDetailsList[i].productCategory === 'Prepaid Maintenance') {
                                            return success();
                                        } else if (rates.product === 'VSC' && rates.data.productDetailsList[i].productCategory === 'Vehicle Service') {
                                            return success();
                                        } else if (sessionStorage.getItem("appFlowState").includes("serviceDrive")) {
                                            return success();
                                        } else if (rates.data.productDetailsList.length === 1) {
                                            this.store.dispatch(ratesLoadedFailed(rates));
                                            this.store.dispatch(hideInlineLoader());
                                            return ratesError();
                                        }
                                    } else {
                                        return success()
                                    }
                                } else if (rates.data.productDetailsList[i].statusCode === 101) {
                                    this.store.dispatch(ratesLoadedFailed(rates))
                                    this.store.dispatch(hideInlineLoader())
                                    return invalidState()
                                } else if (rates.data.productDetailsList[i].statusCode === 102) {
                                    if (brandInfo.isMultiProduct && brandInfo.brand === 'NCESI') {
                                        if (rates.product && rates.product === 'PPM' && rates.data.productDetailsList[i].productName === 'NPPM') {
                                            this.store.dispatch(ratesLoadedFailed(rates))
                                            this.store.dispatch(hideInlineLoader())
                                            return productExist()
                                        } else if (rates.product && rates.product !== 'PPM' && rates.data.productDetailsList[i].productName === 'NASP') {
                                            this.store.dispatch(ratesLoadedFailed(rates))
                                            this.store.dispatch(hideInlineLoader())
                                            return productExist()
                                        }
                                    } else if (brandInfo.brand === "HPP" || brandInfo.brand === "Genesis" || brandInfo.brand === "Kia") {
                                        if (shouldDeferHppGenesisServiceDriveProductOutcome()) {
                                            continue;
                                        }

                                        let request: Vehicle = {
                                            isElectric: null,
                                            make: rates.data.vehicleMake,
                                            makeExternalId: "",
                                            makeSfId: "",
                                            model: rates.data.vehicleModel,
                                            modelExternalId: "",
                                            modelSfId: "",
                                            modelNumber: "",
                                            purchaseDate: "",
                                            inserviceDate: rates.data?.inServiceDate
                                              ? rates.data.inServiceDate
                                              : null,
                                            mfrWarrantyDate: "",
                                            deliveryDate: "",
                                            exteriorColor: "",
                                            referenceNumber1: "",
                                            referenceNumber2: "",
                                            referenceNumber3: "",
                                            referenceNumber4: "",
                                            registeredState: action.customerState,
                                            mileage: action.odometer,
                                            trim: rates.data.vehicleTrim,
                                            vehicleCondition: "",
                                            vehicleExternalId: "",
                                            vehicleSfId: "",
                                            vehicleType: "",
                                            trimExternalId: "",
                                            trimSfId: "",
                                            vin: action.vin,
                                            year: rates.data.vehicleYear,
                                          };
                              
                                          if (_vehicle) {
                                            request.isElectric = _vehicle.isElectric;
                                          }
                                        if (rates.product === "PPM" && rates.data.productDetailsList[i].productCategory === 'Prepaid Maintenance') {
                                          this.store.dispatch(ratesLoadedFailed(rates));
                                          this.store.dispatch(loadVehicle(request));
                                          this.store.dispatch(loadVehicleImage(request));
                                          this.store.dispatch(hideInlineLoader());
                                          return productExist();
                                        } else if (rates.product === "VSC" && rates.data.productDetailsList[i].productCategory === 'Vehicle Service') {
                                          this.store.dispatch(ratesLoadedFailed(rates));
                                          this.store.dispatch(loadVehicle(request));
                                          this.store.dispatch(loadVehicleImage(request));
                                          this.store.dispatch(hideInlineLoader());
                                          return productExist();
                                        } else if (sessionStorage.getItem("appFlowState").includes("serviceDrive")) {
                                            this.store.dispatch(ratesLoadedFailed(rates))
                                            this.store.dispatch(loadVehicle(request));
                                            this.store.dispatch(loadVehicleImage(request));
                                            this.store.dispatch(hideInlineLoader())
                                            return productExist()
                                        } 
                                    } else {
                                        this.store.dispatch(ratesLoadedFailed(rates))
                                        this.store.dispatch(hideInlineLoader())
                                        return productExist()
                                    }

                                } else if (rates.data.productDetailsList[i].statusCode === 105) {
                                    console.log('reading 105', rates.product, rates.data.productDetailsList[i].productCategory)
                                    if (brandInfo.isMultiProduct) {
                                        if (brandInfo.brand === 'HPP' || brandInfo.brand === 'Genesis' || brandInfo.brand === 'Kia') {
                                            if (shouldDeferHppGenesisServiceDriveProductOutcome()) {
                                                continue;
                                            }

                                            if (rates.product === 'PPM' && rates.data.productDetailsList[i].productCategory === 'Prepaid Maintenance') {
                                                this.store.dispatch(ratesLoadedFailed(rates))
                                                this.store.dispatch(hideInlineLoader())
                                                return (mileageOrAgeExceeded())
                                            } else if (rates.product === 'VSC' && rates.data.productDetailsList[i].productCategory === 'Vehicle Service') {
                                                this.store.dispatch(ratesLoadedFailed(rates))
                                                this.store.dispatch(hideInlineLoader())
                                                return (mileageOrAgeExceeded())
                                            } else if (sessionStorage.getItem("appFlowState").includes("serviceDrive")) {
                                                this.store.dispatch(ratesLoadedFailed(rates))
                                                this.store.dispatch(hideInlineLoader())
                                                return (mileageOrAgeExceeded())
                                            }
                                        } else if (brandInfo.brand === "NCESI") {
                                            if (rates.product === "PPM" && rates.data.productDetailsList[i].productCode === "NPPM") {
                                                console.log('reading 105 NPPM')
                                              this.store.dispatch(ratesLoadedFailed(rates));
                                              this.store.dispatch(hideInlineLoader());
                                              return (mileageOrAgeExceeded());
                                            } else if (rates.product !== "PPM" && rates.data.productDetailsList[i].productCode === "NASP") {
                                              this.store.dispatch(ratesLoadedFailed(rates));
                                              this.store.dispatch(hideInlineLoader());
                                              return (mileageOrAgeExceeded());
                                            } else if (rates.data.productDetailsList.length === 1) {
                                              console.log('reading 105 else')
                                              this.store.dispatch(ratesLoadedFailed(rates));
                                              this.store.dispatch(hideInlineLoader());
                                              return (mileageOrAgeExceeded());
                                            }
                                          } else {
                                            this.store.dispatch(ratesLoadedFailed(rates))
                                            this.store.dispatch(hideInlineLoader())
                                            return (mileageOrAgeExceeded());
                                        }
                                    } else {
                                        this.store.dispatch(ratesLoadedFailed(rates))
                                        this.store.dispatch(hideInlineLoader())
                                        return (mileageOrAgeExceeded());
                                    }

                                } else if (rates.data.productDetailsList[i].statusCode === 106) {
                                    console.log('reading 106', rates.product, rates.data.productDetailsList[i].productCategory)
                                    if (brandInfo.brand === "HPP" || brandInfo.brand === "Genesis" || brandInfo.brand === "Kia") {
                                      if (shouldDeferHppGenesisServiceDriveProductOutcome()) {
                                        continue;
                                      }

                                      if (rates.product === "PPM" && rates.data.productDetailsList[i].productCategory === 'Prepaid Maintenance') {
                                        this.store.dispatch(ratesLoadedFailed(rates));
                                        this.store.dispatch(hideInlineLoader());
                                        return hasExistingClaims();
                                      } else if (sessionStorage.getItem("appFlowState").includes("serviceDrive")) {
                                        this.store.dispatch(ratesLoadedFailed(rates))
                                        this.store.dispatch(hideInlineLoader())
                                        return hasExistingClaims();
                                      } else {
                                        // Fallback when conditions don't match
                                        this.store.dispatch(ratesLoadedFailed(rates));
                                        this.store.dispatch(hideInlineLoader());
                                        return hasExistingClaims();
                                      }
                                    } else {
                                      // Fallback for other brands
                                      this.store.dispatch(ratesLoadedFailed(rates));
                                      this.store.dispatch(hideInlineLoader());
                                      return hasExistingClaims();
                                    }
                                  } else if (rates.data.productDetailsList[i].statusCode === 107) {
                                    if (brandInfo.brand === "NCESI") {
                                      if (rates.product === "PPM" && rates.data.productDetailsList[i].productCode === "NPPM") {
                                        this.store.dispatch(ratesLoadedFailed(rates));
                                        this.store.dispatch(hideInlineLoader());
                                        return (ncesiMileageOrAgeExceeded());
                                      } else if (rates.product !== "PPM" && rates.data.productDetailsList[i].productCode === "NASP") {
                                        this.store.dispatch(ratesLoadedFailed(rates));
                                        this.store.dispatch(hideInlineLoader());
                                        return (ncesiMileageOrAgeExceeded());
                                      } else if (rates.data.productDetailsList.length === 1) {
                                        this.store.dispatch(ratesLoadedFailed(rates));
                                        this.store.dispatch(hideInlineLoader());
                                        return (ncesiMileageOrAgeExceeded());
                                      }
                                    } else {
                                      this.store.dispatch(ratesLoadedFailed(rates));
                                      this.store.dispatch(hideInlineLoader());
                                      return (ncesiMileageOrAgeExceeded());
                                    }
                                  }
                            }

                            const vinMixedOutcome = resolveHyundaiServiceDriveAfterLoop(
                              rates
                            );
                            if (vinMixedOutcome && isHppGenesisServiceDriveMultiProduct()) {
                              return applyHyundaiServiceDriveOutcome(
                                vinMixedOutcome,
                                rates,
                                action,
                                _vehicle,
                                this.store.dispatch.bind(this.store),
                                action.odometer
                              );
                            }

                            // Fallback if no status code matched
                            this.store.dispatch(ratesLoadedFailed(rates));
                            this.store.dispatch(hideInlineLoader());
                            return invalidState();
                        }),
                        catchError((error) => {
                            console.log("Error Caught: ", error);

                            this.store.dispatch(hideInlineLoader());
                            this.store.dispatch(ratesError());

                            return EMPTY;

                        })
                    )
                )
            )
    )



    constructor(
        private actions$: Actions,
        private vehicleService: VehicleService,
        private store: Store<{}>
    ) { }
}
